import { createSlice } from '@reduxjs/toolkit';
import {
    getUserList,
    getUserRoles,
    updateUser,
    updateUserStatus,
    deleteUserAccount,
    fetchAdminProductList,
    updateProductStatus,
    createProduct,
    updateProduct,
    getOrderTypes,
    addOrderType,
    fetchNotificationsList,
    updateNotificationStatus,
    fetchNotificationDetails,
    updateNotificationDetails,
    fetchOrderStatuses,
    fetchUserDataWithId,
    getOrderProductSummary,
    getDriverAssignments,
    getOrderTrackingList,
    getDriversList,
    getDeliveryPartnersList,
    getCutOffTimes,
    manageCutOffTime,
    addDriverAssignment,
    updateDriverAssignment,
    deleteDriverAssignment,
    getPreferenceInfo,
    uploadProductImage,
    changeCashStatus,
    changeDeliveryDate,
    changeDeliveryPartner,
    fetchPrintOrderInfo,
    updateOrderItems,
    fetchAdminUserDetails,
    updateProductAttributes,
    updateProductQuantity,
    fetchDriverTimeline,
    adminCorrectWork,
    adminUpdateDriverRate,
    adminSettlePayments,
    adminDeleteWork,
} from '../thunk/adminThunk';
import { fetchProductDetailsById } from '../thunk/productThunk';

const initialState = {
    userList: [],
    userListLoading: false,
    userListError: null,
    userRoles: [],
    userRolesLoading: false,
    userRolesError: null,
    updateUserLoading: false,
    updateUserError: null,
    updateUserStatusLoading: false,
    updateUserStatusError: null,
    deleteUserAccountLoading: false,
    deleteUserAccountError: null,
    adminProductList: [],
    adminProductListLoading: false,
    adminProductListError: null,
    updateProductStatusLoading: false,
    updateProductStatusError: null,
    createProductLoading: false,
    createProductError: null,
    updateProductLoading: false,
    updateProductError: null,
    productDetails: null,
    productDetailsLoading: false,
    productDetailsError: null,
    notificationsList: [],
    notificationsListLoading: false,
    notificationsListError: null,
    updateNotificationStatusLoading: false,
    updateNotificationStatusError: null,
    notificationDetails: null,
    notificationDetailsLoading: false,
    notificationDetailsError: null,
    updateNotificationDetailsLoading: false,
    updateNotificationDetailsError: null,
    orderStatuses: [],
    orderStatusesLoading: false,
    orderStatusesError: null,
    getUserDetailsById: [],
    getUserDetailsByIdLoading: false,
    getUserDetailsByIdError: null,
    orderProductSummary: [],
    orderProductSummaryLoading: false,
    orderProductSummaryError: null,
    driverAssignments: [],
    driverAssignmentsLoading: false,
    driverAssignmentsError: null,
    orderTrackingList: [],
    orderTrackingListLoading: false,
    orderTrackingListError: null,
    driversList: [],
    driversListLoading: false,
    driversListError: null,
    deliveryPartnersList: [],
    deliveryPartnersListLoading: false,
    deliveryPartnersListError: null,
    cutOffTimes: [],
    cutOffTimesLoading: false,
    cutOffTimesError: null,
    manageCutOffTimeLoading: false,
    manageCutOffTimeError: null,
    addDriverAssignmentLoading: false,
    addDriverAssignmentError: null,
    updateDriverAssignmentLoading: false,
    updateDriverAssignmentError: null,
    deleteDriverAssignmentLoading: false,
    deleteDriverAssignmentError: null,
    preferenceInfo: [],
    preferenceInfoLoading: false,
    preferenceInfoError: null,
    uploadProductImageLoading: false,
    uploadProductImageError: null,
    changeCashStatusLoading: false,
    changeCashStatusError: null,
    changeDeliveryDateLoading: false,
    changeDeliveryDateError: null,
    changeDeliveryPartnerLoading: false,
    changeDeliveryPartnerError: null,
    printOrderInfo: null,
    printOrderInfoLoading: false,
    printOrderInfoError: null,
    updateOrderItemsLoading: false,
    updateOrderItemsError: null,
    orderTypes: [],
    orderTypesLoading: false,
    orderTypesError: null,
    addOrderTypeLoading: false,
    addOrderTypeError: null,
    adminUserDetails: null,
    adminUserDetailsLoading: false,
    adminUserDetailsError: null,
    updateProductAttributesLoading: false,
    updateProductAttributesError: null,
    updateProductQuantityLoading: false,
    updateProductQuantityError: null,
    // Driver Work Timeline
    driverTimeline: null,
    driverTimelineLoading: false,
    driverTimelineError: null,
    adminCorrectWorkLoading: false,
    adminCorrectWorkError: null,
    adminUpdateDriverRateLoading: false,
    adminUpdateDriverRateError: null,
    adminSettlePaymentsLoading: false,
    adminSettlePaymentsError: null,
    adminDeleteWorkLoading: false,
    adminDeleteWorkError: null,
    driverDeleteWorkLoading: false,
    driverDeleteWorkError: null,
};

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        clearUserListError: (state) => {
            state.userListError = null;
        },
        clearUserRolesError: (state) => {
            state.userRolesError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUserList.pending, (state) => {
                state.userListLoading = true;
                state.userListError = null;
            })
            .addCase(getUserList.fulfilled, (state, action) => {
                state.userListLoading = false;
                state.userList = action.payload;
                state.userListError = null;
            })
            .addCase(getUserList.rejected, (state, action) => {
                state.userListLoading = false;
                state.userListError = action.payload;
            })
            .addCase(getUserRoles.pending, (state) => {
                state.userRolesLoading = true;
                state.userRolesError = null;
            })
            .addCase(getUserRoles.fulfilled, (state, action) => {
                state.userRolesLoading = false;
                state.userRoles = action.payload;
                state.userRolesError = null;
            })
            .addCase(getUserRoles.rejected, (state, action) => {
                state.userRolesLoading = false;
                state.userRolesError = action.payload;
            })
            .addCase(updateUser.pending, (state) => {
                state.updateUserLoading = true;
                state.updateUserError = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.updateUserLoading = false;
                state.updateUserError = null;
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.updateUserLoading = false;
                state.updateUserError = action.payload;
            })
            .addCase(updateUserStatus.pending, (state) => {
                state.updateUserStatusLoading = true;
                state.updateUserStatusError = null;
            })
            .addCase(updateUserStatus.fulfilled, (state, action) => {
                state.updateUserStatusLoading = false;
                state.updateUserStatusError = null;
            })
            .addCase(updateUserStatus.rejected, (state, action) => {
                state.updateUserStatusLoading = false;
                state.updateUserStatusError = action.payload;
            })
            .addCase(deleteUserAccount.pending, (state) => {
                state.deleteUserAccountLoading = true;
                state.deleteUserAccountError = null;
            })
            .addCase(deleteUserAccount.fulfilled, (state, action) => {
                state.deleteUserAccountLoading = false;
                state.deleteUserAccountError = null;
            })
            .addCase(deleteUserAccount.rejected, (state, action) => {
                state.deleteUserAccountLoading = false;
                state.deleteUserAccountError = action.payload;
            })
            // Admin Product List
            .addCase(fetchAdminProductList.pending, (state) => {
                state.adminProductListLoading = true;
                state.adminProductListError = null;
            })
            .addCase(fetchAdminProductList.fulfilled, (state, action) => {
                state.adminProductListLoading = false;
                state.adminProductList = action.payload;
                state.adminProductListError = null;
            })
            .addCase(fetchAdminProductList.rejected, (state, action) => {
                state.adminProductListLoading = false;
                state.adminProductListError = action.payload;
            })
            // Update Product Status
            .addCase(updateProductStatus.pending, (state) => {
                state.updateProductStatusLoading = true;
                state.updateProductStatusError = null;
            })
            .addCase(updateProductStatus.fulfilled, (state, action) => {
                state.updateProductStatusLoading = false;
                state.updateProductStatusError = null;

                // Update the product status in the local state
                if (state.adminProductList?.data) {
                    const productIndex = state.adminProductList.data.findIndex(
                        (product) => product.product_id === action.payload.product_id,
                    );
                    if (productIndex !== -1) {
                        // Update the HTML status field to reflect the new status
                        const newStatus = action.payload.status;
                        const statusHtml =
                            newStatus === 1
                                ? state.adminProductList.data[productIndex].status
                                    .replace('Disable', 'Enable')
                                    .replace('>', ' checked>')
                                : state.adminProductList.data[productIndex].status
                                    .replace('Enable', 'Disable')
                                    .replace(' checked>', '>');
                        state.adminProductList.data[productIndex].status = statusHtml;
                    }
                }
            })
            .addCase(updateProductStatus.rejected, (state, action) => {
                state.updateProductStatusLoading = false;
                state.updateProductStatusError = action.payload;
            })
            // Create Product
            .addCase(createProduct.pending, (state) => {
                state.createProductLoading = true;
                state.createProductError = null;
            })
            .addCase(createProduct.fulfilled, (state, action) => {
                state.createProductLoading = false;
                state.createProductError = null;
            })
            .addCase(createProduct.rejected, (state, action) => {
                state.createProductLoading = false;
                state.createProductError = action.payload;
            })
            // Update Product
            .addCase(updateProduct.pending, (state) => {
                state.updateProductLoading = true;
                state.updateProductError = null;
            })
            .addCase(updateProduct.fulfilled, (state, action) => {
                state.updateProductLoading = false;
                state.updateProductError = null;
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.updateProductLoading = false;
                state.updateProductError = action.payload;
            })
            // Fetch Product Details
            .addCase(fetchProductDetailsById.pending, (state) => {
                state.productDetailsLoading = true;
                state.productDetailsError = null;
            })
            .addCase(fetchProductDetailsById.fulfilled, (state, action) => {
                state.productDetailsLoading = false;
                state.productDetails = action?.payload?.data;
                state.productDetailsError = null;
            })
            .addCase(fetchProductDetailsById.rejected, (state, action) => {
                state.productDetailsLoading = false;
                state.productDetailsError = action.payload;
                state.productDetails = null;
            })
            // Fetch Notifications List
            .addCase(fetchNotificationsList.pending, (state) => {
                state.notificationsListLoading = true;
                state.notificationsListError = null;
            })
            .addCase(fetchNotificationsList.fulfilled, (state, action) => {
                state.notificationsListLoading = false;
                state.notificationsList = action.payload;
                state.notificationsListError = null;
            })
            .addCase(fetchNotificationsList.rejected, (state, action) => {
                state.notificationsListLoading = false;
                state.notificationsListError = action.payload;
            })
            // Update Notification Status
            .addCase(updateNotificationStatus.pending, (state) => {
                state.updateNotificationStatusLoading = true;
                state.updateNotificationStatusError = null;
            })
            .addCase(updateNotificationStatus.fulfilled, (state, action) => {
                state.updateNotificationStatusLoading = false;
                state.updateNotificationStatusError = null;

                // Update the notification status in the local state
                if (state.notificationsList?.data) {
                    const notificationIndex = state.notificationsList.data.findIndex(
                        (notification) => notification.id === action.payload.notificationId,
                    );
                    if (notificationIndex !== -1) {
                        // Update the HTML status field to reflect the new status
                        const newStatus = action.payload.status;
                        const statusHtml =
                            newStatus === 1
                                ? state.notificationsList.data[notificationIndex].status
                                    .replace('data-status="False"', 'data-status="True"')
                                    .replace('>', ' checked>')
                                : state.notificationsList.data[notificationIndex].status
                                    .replace('data-status="True"', 'data-status="False"')
                                    .replace(' checked>', '>');
                        state.notificationsList.data[notificationIndex].status = statusHtml;
                    }
                }
            })
            .addCase(updateNotificationStatus.rejected, (state, action) => {
                state.updateNotificationStatusLoading = false;
                state.updateNotificationStatusError = action.payload;
            })
            // Fetch Notification Details
            .addCase(fetchNotificationDetails.pending, (state) => {
                state.notificationDetailsLoading = true;
                state.notificationDetailsError = null;
            })
            .addCase(fetchNotificationDetails.fulfilled, (state, action) => {
                state.notificationDetailsLoading = false;
                state.notificationDetails = action.payload;
                state.notificationDetailsError = null;
            })
            .addCase(fetchNotificationDetails.rejected, (state, action) => {
                state.notificationDetailsLoading = false;
                state.notificationDetailsError = action.payload;
                state.notificationDetails = null;
            })
            // Update Notification Details
            .addCase(updateNotificationDetails.pending, (state) => {
                state.updateNotificationDetailsLoading = true;
                state.updateNotificationDetailsError = null;
            })
            .addCase(updateNotificationDetails.fulfilled, (state, action) => {
                state.updateNotificationDetailsLoading = false;
                state.updateNotificationDetailsError = null;
            })
            .addCase(updateNotificationDetails.rejected, (state, action) => {
                state.updateNotificationDetailsLoading = false;
                state.updateNotificationDetailsError = action.payload;
            })
            // Fetch Order Statuses
            .addCase(fetchOrderStatuses.pending, (state) => {
                state.orderStatusesLoading = true;
                state.orderStatusesError = null;
            })
            .addCase(fetchOrderStatuses.fulfilled, (state, action) => {
                state.orderStatusesLoading = false;
                state.orderStatuses = action.payload;
                state.orderStatusesError = null;
            })
            .addCase(fetchOrderStatuses.rejected, (state, action) => {
                state.orderStatusesLoading = false;
                state.orderStatusesError = action.payload;
            })
            // Fetch User data for Delivery partner
            .addCase(fetchUserDataWithId.pending, (state) => {
                state.getUserDetailsByIdLoading = true;
                state.getUserDetailsByIdError = null;
            })
            .addCase(fetchUserDataWithId.fulfilled, (state, action) => {
                state.getUserDetailsByIdLoading = false;
                state.getUserDetailsById = action.payload;
                state.getUserDetailsByIdError = null;
            })
            .addCase(fetchUserDataWithId.rejected, (state, action) => {
                state.getUserDetailsByIdLoading = false;
                state.getUserDetailsByIdError = action.payload;
            })
            // Order Product Summary
            .addCase(getOrderProductSummary.pending, (state) => {
                state.orderProductSummaryLoading = true;
                state.orderProductSummaryError = null;
            })
            .addCase(getOrderProductSummary.fulfilled, (state, action) => {
                state.orderProductSummaryLoading = false;
                state.orderProductSummary = action.payload;
                state.orderProductSummaryError = null;
            })
            .addCase(getOrderProductSummary.rejected, (state, action) => {
                state.orderProductSummaryLoading = false;
                state.orderProductSummaryError = action.payload;
            })
            // Driver Assignments
            .addCase(getDriverAssignments.pending, (state) => {
                state.driverAssignmentsLoading = true;
                state.driverAssignmentsError = null;
            })
            .addCase(getDriverAssignments.fulfilled, (state, action) => {
                state.driverAssignmentsLoading = false;
                state.driverAssignments = action.payload;
                state.driverAssignmentsError = null;
            })
            .addCase(getDriverAssignments.rejected, (state, action) => {
                state.driverAssignmentsLoading = false;
                state.driverAssignmentsError = action.payload;
            })
            // Order Tracking List
            .addCase(getOrderTrackingList.pending, (state) => {
                state.orderTrackingListLoading = true;
                state.orderTrackingListError = null;
            })
            .addCase(getOrderTrackingList.fulfilled, (state, action) => {
                state.orderTrackingListLoading = false;
                state.orderTrackingList = action.payload;
                state.orderTrackingListError = null;
            })
            .addCase(getOrderTrackingList.rejected, (state, action) => {
                state.orderTrackingListLoading = false;
                state.orderTrackingListError = action.payload;
            })
            // Drivers List
            .addCase(getDriversList.pending, (state) => {
                state.driversListLoading = true;
                state.driversListError = null;
            })
            .addCase(getDriversList.fulfilled, (state, action) => {
                state.driversListLoading = false;
                state.driversList = action.payload;
                state.driversListError = null;
            })
            .addCase(getDriversList.rejected, (state, action) => {
                state.driversListLoading = false;
                state.driversListError = action.payload;
            })
            // Delivery Partners List
            .addCase(getDeliveryPartnersList.pending, (state) => {
                state.deliveryPartnersListLoading = true;
                state.deliveryPartnersListError = null;
            })
            .addCase(getDeliveryPartnersList.fulfilled, (state, action) => {
                state.deliveryPartnersListLoading = false;
                state.deliveryPartnersList = action.payload;
                state.deliveryPartnersListError = null;
            })
            .addCase(getDeliveryPartnersList.rejected, (state, action) => {
                state.deliveryPartnersListLoading = false;
                state.deliveryPartnersListError = action.payload;
            })
            // Cut-off Times
            .addCase(getCutOffTimes.pending, (state) => {
                state.cutOffTimesLoading = true;
                state.cutOffTimesError = null;
            })
            .addCase(getCutOffTimes.fulfilled, (state, action) => {
                state.cutOffTimesLoading = false;
                state.cutOffTimes = action.payload;
                state.cutOffTimesError = null;
            })
            .addCase(getCutOffTimes.rejected, (state, action) => {
                state.cutOffTimesLoading = false;
                state.cutOffTimesError = action.payload;
            })
            // Manage Cut-off Time
            .addCase(manageCutOffTime.pending, (state) => {
                state.manageCutOffTimeLoading = true;
                state.manageCutOffTimeError = null;
            })
            .addCase(manageCutOffTime.fulfilled, (state, action) => {
                state.manageCutOffTimeLoading = false;
                state.manageCutOffTimeError = null;
            })
            .addCase(manageCutOffTime.rejected, (state, action) => {
                state.manageCutOffTimeLoading = false;
                state.manageCutOffTimeError = action.payload;
            })
            // Add Driver Assignment
            .addCase(addDriverAssignment.pending, (state) => {
                state.addDriverAssignmentLoading = true;
                state.addDriverAssignmentError = null;
            })
            .addCase(addDriverAssignment.fulfilled, (state, action) => {
                state.addDriverAssignmentLoading = false;
                state.addDriverAssignmentError = null;
            })
            .addCase(addDriverAssignment.rejected, (state, action) => {
                state.addDriverAssignmentLoading = false;
                state.addDriverAssignmentError = action.payload;
            })
            // Update Driver Assignment
            .addCase(updateDriverAssignment.pending, (state) => {
                state.updateDriverAssignmentLoading = true;
                state.updateDriverAssignmentError = null;
            })
            .addCase(updateDriverAssignment.fulfilled, (state, action) => {
                state.updateDriverAssignmentLoading = false;
                state.updateDriverAssignmentError = null;
            })
            .addCase(updateDriverAssignment.rejected, (state, action) => {
                state.updateDriverAssignmentLoading = false;
                state.updateDriverAssignmentError = action.payload;
            })
            // Delete Driver Assignment
            .addCase(deleteDriverAssignment.pending, (state) => {
                state.deleteDriverAssignmentLoading = true;
                state.deleteDriverAssignmentError = null;
            })
            .addCase(deleteDriverAssignment.fulfilled, (state, action) => {
                state.deleteDriverAssignmentLoading = false;
                state.deleteDriverAssignmentError = null;
            })
            .addCase(deleteDriverAssignment.rejected, (state, action) => {
                state.deleteDriverAssignmentLoading = false;
                state.deleteDriverAssignmentError = action.payload;
            })
            .addCase(getPreferenceInfo.pending, (state) => {
                state.preferenceInfoLoading = true;
                state.preferenceInfoError = null;
            })
            .addCase(getPreferenceInfo.fulfilled, (state, action) => {
                state.preferenceInfoLoading = false;
                state.preferenceInfo = action.payload?.message || [];
            })
            .addCase(getPreferenceInfo.rejected, (state, action) => {
                state.preferenceInfoLoading = false;
                state.preferenceInfoError = action.payload;
            })
            // Upload Product Image
            .addCase(uploadProductImage.pending, (state) => {
                state.uploadProductImageLoading = true;
                state.uploadProductImageError = null;
            })
            .addCase(uploadProductImage.fulfilled, (state, action) => {
                state.uploadProductImageLoading = false;
                state.uploadProductImageError = null;
            })
            .addCase(uploadProductImage.rejected, (state, action) => {
                state.uploadProductImageLoading = false;
                state.uploadProductImageError = action.payload;
            })
            // Change Cash Status
            .addCase(changeCashStatus.pending, (state) => {
                state.changeCashStatusLoading = true;
                state.changeCashStatusError = null;
            })
            .addCase(changeCashStatus.fulfilled, (state, action) => {
                state.changeCashStatusLoading = false;
                state.changeCashStatusError = null;
            })
            .addCase(changeCashStatus.rejected, (state, action) => {
                state.changeCashStatusLoading = false;
                state.changeCashStatusError = action.payload;
            })
            // Change Delivery Date
            .addCase(changeDeliveryDate.pending, (state) => {
                state.changeDeliveryDateLoading = true;
                state.changeDeliveryDateError = null;
            })
            .addCase(changeDeliveryDate.fulfilled, (state, action) => {
                state.changeDeliveryDateLoading = false;
                state.changeDeliveryDateError = null;
            })
            .addCase(changeDeliveryDate.rejected, (state, action) => {
                state.changeDeliveryDateLoading = false;
                state.changeDeliveryDateError = action.payload;
            })
            // Change Delivery Partner
            .addCase(changeDeliveryPartner.pending, (state) => {
                state.changeDeliveryPartnerLoading = true;
                state.changeDeliveryPartnerError = null;
            })
            .addCase(changeDeliveryPartner.fulfilled, (state, action) => {
                state.changeDeliveryPartnerLoading = false;
                state.changeDeliveryPartnerError = null;
            })
            .addCase(changeDeliveryPartner.rejected, (state, action) => {
                state.changeDeliveryPartnerLoading = false;
                state.changeDeliveryPartnerError = action.payload;
            })
            // Fetch Print Order Info
            .addCase(fetchPrintOrderInfo.pending, (state) => {
                state.printOrderInfoLoading = true;
                state.printOrderInfoError = null;
            })
            .addCase(fetchPrintOrderInfo.fulfilled, (state, action) => {
                state.printOrderInfoLoading = false;
                state.printOrderInfo = action?.payload?.data;
                state.printOrderInfoError = null;
            })
            .addCase(fetchPrintOrderInfo.rejected, (state, action) => {
                state.printOrderInfoLoading = false;
                state.printOrderInfoError = action.payload;
            })
            // Update Order Items
            .addCase(updateOrderItems.pending, (state) => {
                state.updateOrderItemsLoading = true;
                state.updateOrderItemsError = null;
            })
            .addCase(updateOrderItems.fulfilled, (state, action) => {
                state.updateOrderItemsLoading = false;
                state.updateOrderItemsError = null;
            })
            .addCase(updateOrderItems.rejected, (state, action) => {
                state.updateOrderItemsLoading = false;
                state.updateOrderItemsError = action.payload;
            })
            // Get Order Types
            .addCase(getOrderTypes.pending, (state) => {
                state.orderTypesLoading = true;
                state.orderTypesError = null;
            })
            .addCase(getOrderTypes.fulfilled, (state, action) => {
                state.orderTypesLoading = false;
                state.orderTypes = action.payload?.data || [];
                state.orderTypesError = null;
            })
            .addCase(getOrderTypes.rejected, (state, action) => {
                state.orderTypesLoading = false;
                state.orderTypesError = action.payload;
            })
            // Add Order Type
            .addCase(addOrderType.pending, (state) => {
                state.addOrderTypeLoading = true;
                state.addOrderTypeError = null;
            })
            .addCase(addOrderType.fulfilled, (state, action) => {
                state.addOrderTypeLoading = false;
                state.addOrderTypeError = null;
            })
            .addCase(addOrderType.rejected, (state, action) => {
                state.addOrderTypeLoading = false;
                state.addOrderTypeError = action.payload;
            })
            // Fetch Admin User Details
            .addCase(fetchAdminUserDetails.pending, (state) => {
                state.adminUserDetailsLoading = true;
                state.adminUserDetailsError = null;
            })
            .addCase(fetchAdminUserDetails.fulfilled, (state, action) => {
                state.adminUserDetailsLoading = false;
                state.adminUserDetails = action.payload;
                state.adminUserDetailsError = null;
            })
            .addCase(fetchAdminUserDetails.rejected, (state, action) => {
                state.adminUserDetailsLoading = false;
                state.adminUserDetailsError = action.payload;
                state.adminUserDetails = null;
            })
            // Update Product Attributes
            .addCase(updateProductAttributes.pending, (state) => {
                state.updateProductAttributesLoading = true;
                state.updateProductAttributesError = null;
            })
            .addCase(updateProductAttributes.fulfilled, (state, action) => {
                state.updateProductAttributesLoading = false;
                state.updateProductAttributesError = null;

                // Update the product out_of_stock in the local state
                if (state.adminProductList?.data) {
                    const productIndex = state.adminProductList.data.findIndex(
                        (product) => product.product_id === action.payload.product_id
                    );
                    if (productIndex !== -1) {
                        // Update the out_of_stock field
                        state.adminProductList.data[productIndex].out_of_stock =
                            action.payload.out_of_stock === 1 ? "Yes" : "No";
                    }
                }
            })
            .addCase(updateProductAttributes.rejected, (state, action) => {
                state.updateProductAttributesLoading = false;
                state.updateProductAttributesError = action.payload;
            })
            // Update Product Quantity
            .addCase(updateProductQuantity.pending, (state) => {
                state.updateProductQuantityLoading = true;
                state.updateProductQuantityError = null;
            })
            .addCase(updateProductQuantity.fulfilled, (state, action) => {
                state.updateProductQuantityLoading = false;
                state.updateProductQuantityError = null;

                // Update the product quantity in the local state
                if (state.adminProductList?.data) {
                    const productIndex = state.adminProductList.data.findIndex(
                        (product) => product.product_id === action.payload.product_id
                    );
                    if (productIndex !== -1) {
                        state.adminProductList.data[productIndex].quantity =
                            action.payload.quantity;
                    }
                }
            })
            .addCase(updateProductQuantity.rejected, (state, action) => {
                state.updateProductQuantityLoading = false;
                state.updateProductQuantityError = action.payload;
            })
            // Fetch Driver Timeline
            .addCase(fetchDriverTimeline.pending, (state) => {
                state.driverTimelineLoading = true;
                state.driverTimelineError = null;
                state.driverTimeline = null;
            })
            .addCase(fetchDriverTimeline.fulfilled, (state, action) => {
                state.driverTimelineLoading = false;
                state.driverTimeline = action.payload;
                state.driverTimelineError = null;
            })
            .addCase(fetchDriverTimeline.rejected, (state, action) => {
                state.driverTimelineLoading = false;
                state.driverTimelineError = action.payload;
            })
            // Admin Correct Work
            .addCase(adminCorrectWork.pending, (state) => {
                state.adminCorrectWorkLoading = true;
                state.adminCorrectWorkError = null;
            })
            .addCase(adminCorrectWork.fulfilled, (state) => {
                state.adminCorrectWorkLoading = false;
                state.adminCorrectWorkError = null;
            })
            .addCase(adminCorrectWork.rejected, (state, action) => {
                state.adminCorrectWorkLoading = false;
                state.adminCorrectWorkError = action.payload;
            })
            // Admin Update Driver Rate
            .addCase(adminUpdateDriverRate.pending, (state) => {
                state.adminUpdateDriverRateLoading = true;
                state.adminUpdateDriverRateError = null;
            })
            .addCase(adminUpdateDriverRate.fulfilled, (state) => {
                state.adminUpdateDriverRateLoading = false;
                state.adminUpdateDriverRateError = null;
            })
            .addCase(adminUpdateDriverRate.rejected, (state, action) => {
                state.adminUpdateDriverRateLoading = false;
                state.adminUpdateDriverRateError = action.payload;
            })
            // Admin Settle Payments
            .addCase(adminSettlePayments.pending, (state) => {
                state.adminSettlePaymentsLoading = true;
                state.adminSettlePaymentsError = null;
            })
            .addCase(adminSettlePayments.fulfilled, (state) => {
                state.adminSettlePaymentsLoading = false;
                state.adminSettlePaymentsError = null;
            })
            .addCase(adminSettlePayments.rejected, (state, action) => {
                state.adminSettlePaymentsLoading = false;
                state.adminSettlePaymentsError = action.payload;
            })
            // Admin Delete Work
            .addCase(adminDeleteWork.pending, (state) => {
                state.adminDeleteWorkLoading = true;
                state.adminDeleteWorkError = null;
            })
            .addCase(adminDeleteWork.fulfilled, (state) => {
                state.adminDeleteWorkLoading = false;
                state.adminDeleteWorkError = null;
            })
            .addCase(adminDeleteWork.rejected, (state, action) => {
                state.adminDeleteWorkLoading = false;
                state.adminDeleteWorkError = action.payload;
            })
    }
});

export const { clearUserListError, clearUserRolesError } = adminSlice.actions;
export default adminSlice.reducer;
